import React from "react";
import { useRoutes } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Provider } from "react-redux";
import { store, persistor } from "./redux/store";
import { PersistGate } from "redux-persist/integration/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./assets/styles/index.css";
import routes from "./routes";
import { AuthProvider } from "./contexts/JWTContext";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "./pages/error";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { menuItemClasses } from "@mui/material";

const App = () => {
  const content = useRoutes(routes);

  const theme = createTheme({
    typography: {
      fontFamily: "var(--bs-font-sans-serif)",
      body1: {
        fontSize: "11px",
      },
      caption: {
        fontSize: "11px",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          contained: {
            fontSize: "0.825rem",
            fontWeight: 400,
            textTransform: "none",
            boxShadow: "none",
            borderRadius: "5px",
            ":hover": {
              boxShadow: "none",
            },
            ":active": {
              boxShadow: "none",
            },
            ":disabled": {
              opacity: 0.65,
              pointerEvents: "none",
            },
          },
          outlined: {
            fontSize: "0.825rem",
            fontWeight: 400,
            textTransform: "none",
            boxShadow: "none",
            borderRadius: "5px",
            ":hover": {
              boxShadow: "none",
            },
            ":active": {
              boxShadow: "none",
            },
            ":disabled": {
              opacity: 0.65,
              pointerEvents: "none",
            },
          },
          text: {
            fontSize: "0.825rem",
            fontWeight: 400,
            textTransform: "none",
            boxShadow: "none",
            ":hover": {
              boxShadow: "none",
              backgroundColor: "transparent"
            },
            ":active": {
              boxShadow: "none",
            },
            ":disabled": {
              opacity: 0.65,
              pointerEvents: "none",
            },
          },
          sizeSmall: {
            padding: "0.11rem 0.11rem",
            fontSize: "11px",
            borderRadius: "8px",
          },
          sizeLarge: {
            padding: "0.35rem 1rem",
            fontSize: "0.95rem",
            borderRadius: "0.3rem",
          }
        },
        defaultProps: {
          disableRipple: true
        }
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            fontSize: "11px",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          fontSize: "11px"
        }
      },
      MuiTooltip: {
        styleOverrides: {
          arrow: {
            color: "#000"
          },
          tooltip: {
            backgroundColor: "#000",
            fontSize: "12px",
          }
        },
        defaultProps: {
          arrow: true,
          placement: "top",
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: "#495057",
            fontSize: "0.825rem",
            fontWeight: 400,
            padding: "0.35rem 1.5rem",
            transition: "background 0.1s ease-in-out, color 0.1s ease-in-out",
            
            ":hover": {
              color: "#42484e",
              textDecoration: "none",
              backgroundColor: "#f4f7f9",
            },

            ":active": {
              color: "#fff",
              textDecoration: "none",
              backgroundColor: "#3F80EA",
            },

            [`&.${menuItemClasses.disabled}`]: {
              color: "#adb5bd",
              backgroundColor: "transparent",
              opacity: 1
            }
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            padding: 0,
            minWidth: "10rem",
            border: "1px solid rgba(0, 0, 0, 0.15)",
            zIndex: 1000,
            boxShadow: "none",
          },
          root: {
            transform: "translate(0, 2px)"
          }
        },
      },
    },
  });

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <Helmet titleTemplate="%s" defaultTitle="Datajolt" />
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <AuthProvider>
              <ToastContainer
                position="top-right"
                closeOnClick
                pauseOnHover
                pauseOnFocusLoss
                style={{ height: "50px", minHeight: "50px" }}
              />
              <ErrorBoundary 
                FallbackComponent={process.env.NODE_ENV === 'development' ? 
                  ({ error, errorInfo }) => (
                    <div style={{ padding: '20px', backgroundColor: '#ffebee', color: '#c62828', fontFamily: 'monospace' }}>
                      <h2>Development Error:</h2>
                      <pre>{error.message}</pre>
                      <pre>{error.stack}</pre>
                    </div>
                  ) : 
                  ErrorFallback
                }
                onError={(error, errorInfo) => console.error('ErrorBoundary caught an error:', error, errorInfo)}
              >
                {content}
              </ErrorBoundary>
            </AuthProvider>
          </PersistGate>
        </Provider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
